'use client';

import { mapContractTransactionRequestToEip5792Call } from '@revoke.cash/core/eip5792';
import ContentPageLayout from 'app/layouts/ContentPageLayout';
import Button from 'components/common/Button';
import { useState } from 'react';
import { isAddress } from 'viem';
import { useConnection, usePublicClient, useWalletClient } from 'wagmi';
import { prepareApprove } from '../approve/lib';

const ApprovePage = () => {
  const { data: walletClient } = useWalletClient();
  const { address: account } = useConnection();
  const publicClient = usePublicClient()!;
  const [allowancesCsv, setAllowancesCsv] = useState<string>('');

  const handleApprove = async () => {
    const allowances = parseAllowancesCsv(allowancesCsv);

    const transactionRequests = await Promise.all(
      allowances.map(async (allowance) => prepareApprove(allowance as any, publicClient, walletClient!, account!)),
    );

    const calls = transactionRequests.map((request) => mapContractTransactionRequestToEip5792Call(request));

    const { id } = await walletClient!.sendCalls({
      calls,
    });

    await walletClient!.waitForCallsStatus({
      id,
    });
  };

  return (
    <ContentPageLayout>
      <div className="flex flex-col gap-4 max-w-3xl mx-auto">
        <h1>Approve Arbitrary Contracts</h1>
        <p>For testing purposes only.</p>
        <p>Approval Type, Token Address, Spender Address, (Amount/Token ID), (Expiration), (Permit2 Address)</p>
        <textarea
          className="border border-zinc-300 dark:border-zinc-700 rounded-md p-2"
          placeholder="erc20,0x7EA68721984E8E24932E8928106cA9005B3a4786,0xd98B590ebE0a3eD8C144170bA4122D402182976f,1000000000000000000000000000"
          value={allowancesCsv}
          onChange={(e) => setAllowancesCsv(e.target.value)}
        />
        <Button size="md" style="secondary" onClick={handleApprove}>
          Approve All
        </Button>
      </div>
    </ContentPageLayout>
  );
};

export default ApprovePage;

const parseAllowancesCsv = (csv: string) => {
  const lines = csv.split('\n').filter((line) => line.trim() !== '');
  const allowances = lines.map((line, index) => {
    const [allowanceType, tokenAddress, spenderAddress, amountOrTokenId, expiration, permit2Address] = line.split(',');
    if (
      !['erc20', 'erc721_all', 'erc721_single', 'permit2'].includes(allowanceType) ||
      !isAddress(tokenAddress) ||
      !isAddress(spenderAddress)
    ) {
      throw new Error(`Invalid approval type, token address or spender address on line ${index + 1}`);
    }

    if (allowanceType === 'erc20') {
      if (!Number.isInteger(Number(amountOrTokenId))) {
        throw new Error(`Invalid amount on line ${index + 1}`);
      }

      return {
        allowanceType: 'erc20',
        tokenAddress,
        spenderAddress,
        amount: BigInt(amountOrTokenId),
      };
    }

    if (allowanceType === 'erc721_all') {
      return {
        allowanceType: 'erc721_all',
        tokenAddress,
        spenderAddress,
      };
    }

    if (allowanceType === 'erc721_single') {
      if (!Number.isInteger(Number(amountOrTokenId))) {
        throw new Error(`Invalid token ID on line ${index + 1}`);
      }

      return {
        allowanceType: 'erc721_single',
        tokenAddress,
        spenderAddress,
        tokenId: BigInt(amountOrTokenId),
      };
    }

    if (allowanceType === 'permit2') {
      if (
        !isAddress(permit2Address) ||
        !Number.isInteger(Number(amountOrTokenId)) ||
        !Number.isInteger(Number(expiration))
      ) {
        throw new Error(`Invalid permit2 address, amount or expiration on line ${index + 1}`);
      }

      return {
        allowanceType: 'permit2',
        tokenAddress,
        spenderAddress,
        amount: BigInt(amountOrTokenId),
        expiration: BigInt(expiration),
        permit2Address,
      };
    }

    throw new Error(`Invalid allowance type on line ${index + 1}`);
  });

  return allowances;
};
